import { useEffect, useState } from 'react';
import { CreditCard, Search, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchSubscriptions,
  updateSubscription,
  assignSubscription,
  setStatusFilter,
  setTierFilter,
  setSelectedSubscription,
} from '@/features/admin';
import type {
  Subscription,
  SubscriptionTier,
  SubscriptionStatus,
  UpdateSubscriptionRequest,
  AssignSubscriptionRequest,
} from '@/features/admin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

// Tier badge color mapping
const tierColors: Record<SubscriptionTier, string> = {
  free: 'bg-gray-500',
  silver: 'bg-gray-400',
  gold: 'bg-amber-500',
  platinum: 'bg-purple-600',
};

// Status badge variant mapping
const statusVariants: Record<SubscriptionStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  active: 'default',
  canceled: 'secondary',
  expired: 'destructive',
  past_due: 'outline',
};

export default function SubscriptionsPage() {
  const dispatch = useAppDispatch();
  const subscriptions = useAppSelector((state) => state.subscriptions?.subscriptions ?? []);
  const isLoading = useAppSelector((state) => state.subscriptions?.isLoading ?? false);
  const isUpdating = useAppSelector((state) => state.subscriptions?.isUpdating ?? false);
  const statusFilter = useAppSelector((state) => state.subscriptions?.statusFilter ?? 'all');
  const tierFilter = useAppSelector((state) => state.subscriptions?.tierFilter ?? 'all');
  const selectedSubscription = useAppSelector((state) => state.subscriptions?.selectedSubscription ?? null);

  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState<UpdateSubscriptionRequest>({});

  // Assign form state
  const [assignForm, setAssignForm] = useState<AssignSubscriptionRequest>({
    userId: '',
    tier: 'free',
  });

  // Fetch subscriptions on mount and when filters change
  useEffect(() => {
    const params: { status?: SubscriptionStatus; tier?: SubscriptionTier } = {};
    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }
    if (tierFilter !== 'all') {
      params.tier = tierFilter;
    }
    dispatch(fetchSubscriptions(Object.keys(params).length > 0 ? params : undefined));
  }, [dispatch, statusFilter, tierFilter]);

  // Filter subscriptions by search term
  const filteredSubscriptions = subscriptions.filter((sub) => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = sub.user ? `${sub.user.firstName} ${sub.user.lastName}`.toLowerCase() : '';
    const email = sub.user?.email?.toLowerCase() ?? '';
    return (
      fullName.includes(searchLower) ||
      email.includes(searchLower) ||
      sub.userId.toLowerCase().includes(searchLower) ||
      sub.id.toLowerCase().includes(searchLower)
    );
  });

  const handleEditClick = (subscription: Subscription) => {
    dispatch(setSelectedSubscription(subscription));
    setEditForm({
      tier: subscription.tier,
      status: subscription.status,
      endDate: subscription.endDate,
      renewalDate: subscription.renewalDate,
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedSubscription) return;

    try {
      await dispatch(
        updateSubscription({
          id: selectedSubscription.id,
          data: editForm,
        }),
      ).unwrap();
      toast.success('Subscription updated successfully');
      setEditDialogOpen(false);
    } catch (err) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Failed to update subscription');
    }
  };

  const handleAssignSubmit = async () => {
    if (!assignForm.userId) {
      toast.error('User ID is required');
      return;
    }

    try {
      await dispatch(assignSubscription(assignForm)).unwrap();
      toast.success('Subscription assigned successfully');
      setAssignDialogOpen(false);
      setAssignForm({ userId: '', tier: 'free' });
    } catch (err) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Failed to assign subscription');
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTierBadge = (tier: SubscriptionTier) => (
    <Badge className={`${tierColors[tier]} text-white capitalize`}>{tier}</Badge>
  );

  const getStatusBadge = (status: SubscriptionStatus) => (
    <Badge variant={statusVariants[status]} className="capitalize">
      {status.replace('_', ' ')}
    </Badge>
  );

  if (isLoading && subscriptions.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size={80} />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      {/* Page Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Subscription Management</h1>
          <p className="text-muted-foreground">View and manage user subscriptions</p>
        </div>
        <Button onClick={() => setAssignDialogOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Assign Subscription
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-4">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search by name, email, or user ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => dispatch(setStatusFilter(value as SubscriptionStatus | 'all'))}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="past_due">Past Due</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={tierFilter}
          onValueChange={(value) => dispatch(setTierFilter(value as SubscriptionTier | 'all'))}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="silver">Silver</SelectItem>
            <SelectItem value="gold">Gold</SelectItem>
            <SelectItem value="platinum">Platinum</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscriptions ({filteredSubscriptions.length})
          </CardTitle>
          <CardDescription>Click on a row to edit subscription details</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredSubscriptions.length === 0 ? (
            <div className="border-muted-foreground/25 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center">
              <CreditCard className="text-muted-foreground mb-2 h-8 w-8" />
              <h3 className="font-medium">No subscriptions found</h3>
              <p className="text-muted-foreground text-sm">
                {searchTerm ? 'Try adjusting your search criteria' : 'No subscriptions match the current filter'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubscriptions.map((subscription) => (
                  <TableRow
                    key={subscription.id}
                    className="cursor-pointer"
                    onClick={() => handleEditClick(subscription)}
                  >
                    <TableCell>
                      <div>
                        {subscription.user?.firstName || subscription.user?.lastName ? (
                          <>
                            <p className="font-medium">
                              {subscription.user.firstName} {subscription.user.lastName}
                            </p>
                            <p className="text-muted-foreground text-sm">{subscription.user.email}</p>
                          </>
                        ) : (
                          <p className="text-muted-foreground font-mono text-xs">{subscription.userId}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getTierBadge(subscription.tier)}</TableCell>
                    <TableCell>{getStatusBadge(subscription.status)}</TableCell>
                    <TableCell>{formatDate(subscription.startDate)}</TableCell>
                    <TableCell>{formatDate(subscription.endDate)}</TableCell>
                    <TableCell>{formatDate(subscription.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Subscription Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Subscription</DialogTitle>
            <DialogDescription>
              Update subscription for{' '}
              {selectedSubscription?.user?.firstName || selectedSubscription?.user?.lastName ? (
                <span className="font-medium">
                  {selectedSubscription.user.firstName} {selectedSubscription.user.lastName}
                </span>
              ) : (
                <span className="font-mono text-xs">{selectedSubscription?.userId}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="tier">Tier</Label>
              <Select
                value={editForm.tier}
                onValueChange={(value) => setEditForm({ ...editForm, tier: value as SubscriptionTier })}
              >
                <SelectTrigger id="tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({ ...editForm, status: value as SubscriptionStatus })}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={editForm.endDate?.split('T')[0] || ''}
                onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="renewalDate">Renewal Date</Label>
              <Input
                id="renewalDate"
                type="date"
                value={editForm.renewalDate?.split('T')[0] || ''}
                onChange={(e) => setEditForm({ ...editForm, renewalDate: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={isUpdating}>
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Subscription Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Subscription</DialogTitle>
            <DialogDescription>Assign a subscription tier to a user</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="userId">User ID</Label>
              <Input
                id="userId"
                placeholder="Enter user ID"
                value={assignForm.userId}
                onChange={(e) => setAssignForm({ ...assignForm, userId: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="assignTier">Tier</Label>
              <Select
                value={assignForm.tier}
                onValueChange={(value) => setAssignForm({ ...assignForm, tier: value as SubscriptionTier })}
              >
                <SelectTrigger id="assignTier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="assignEndDate">End Date (optional)</Label>
              <Input
                id="assignEndDate"
                type="date"
                value={assignForm.endDate || ''}
                onChange={(e) => setAssignForm({ ...assignForm, endDate: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="assignRenewalDate">Renewal Date (optional)</Label>
              <Input
                id="assignRenewalDate"
                type="date"
                value={assignForm.renewalDate || ''}
                onChange={(e) => setAssignForm({ ...assignForm, renewalDate: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignSubmit} disabled={isUpdating}>
              {isUpdating ? 'Assigning...' : 'Assign Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
